import { BaseQueryFn, FetchArgs, FetchBaseQueryError, createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import Config from '../helpers/Config'
import { userActions } from '../stateManagment/features/userSlice';
import { cartActions } from '../stateManagment/features/cartSlice';
import { Mutex } from 'async-mutex'
import { logFetchError, logApiError } from '../helpers/TelemetryHelper'

const mutex = new Mutex()
type SiteConfigRetryExtraOptions = {
    __siteConfigRetried?: boolean
}
// Create our baseQuery instance
const baseQuery = (baseUrl: string) => fetchBaseQuery({
    baseUrl,
    responseHandler: async (response) => {
        let data: any
        try {
            data = await response.json()
        } catch (e) {
            logApiError('RESPONSE_NOT_JSON', response.url, {
                httpStatus: response.status,
                contentType: response.headers?.get('content-type'),
                errorMessage: (e as any)?.message ?? String(e),
            })
            throw e
        }
        if (response.ok) {
            try {
                data.data = JSON.parse(data.data);
            } catch (e) {
                if (response.url?.includes('site-config') || response.url?.includes('site_config')) {
                    logApiError('SITE_CONFIG_DATA_PARSE_FAIL', response.url, {
                        httpStatus: response.status,
                        dataType: typeof data.data,
                        dataPreview: typeof data.data === 'string' ? data.data.substring(0, 200) : null,
                        bodyStatusCode: data.statusCode,
                    })
                }
            }
        }
        return data
    },

    prepareHeaders: (headers, { getState }) => {
        // toggleSizing (ToggleProvider): true = allotment, false = self-pay.
        // Headers must match: allotment → SiteID=*_S + SizingMode=true; self-pay → base SiteID + SizingMode=false.
        const rawToggle = localStorage.getItem('toggleSizing')
        let isAllotmentPerspective = false
        if (rawToggle != null) {
            try {
                isAllotmentPerspective = JSON.parse(rawToggle) === true
            } catch {
                isAllotmentPerspective = rawToggle === 'true'
            }
        }

        // By default, if we have a token in the store, let's use that for authenticated requests
        try {
            const { 
                UserID: userid, Login: login, CartID: cartId, EcomCustNo: ecomCustNo, 
                JWTToken: token, JWTRefreshToken: refreshToken, CustDefaultSc: defaultSc, CustHqAccount: hqAccount, 
                BECustNo: beCustNumber, EmployeeID: employeeId, CompanyNumber: companyNbr, 
                EmployeeLocation: consumerLocation, SelectedAllotmentPackageID: selectedAllotment, AllotmentPerepectiveEnabled: allotmentPerepectiveEnabled
            } = (getState() as any).userReducer.user;
            if (userid) {
                headers.set('UserID', userid)
            }
            if (cartId) {
                headers.set('CartID', cartId)
            }
            if (ecomCustNo) {
                headers.set('EcomCustNo', ecomCustNo)
            }
            if (defaultSc) {
                headers.set('SiteDefaultSC', defaultSc)
            }
            if (hqAccount) {
                headers.set('SiteHqAccount', hqAccount)
            }
            if (beCustNumber) {
                headers.set('BECustNo', beCustNumber)
            }
            if (companyNbr) {
                headers.set('CompanyNumber', companyNbr.toString())
            }
            if (consumerLocation) {
                headers.set('ConsumerLocation', consumerLocation)
            }
            // SizingMode must align with SiteID / allotment vs self-pay (see below)
            if (isAllotmentPerspective) {
                headers.set('SizingMode', 'true')
            } else {
                headers.set('SizingMode', 'false')
            }

            if (employeeId) {
                headers.set('EmployeeID', employeeId)
            }
            headers.set('SelectedAllotmentPackageID', selectedAllotment ?? 0)

            headers.set('Token', token ?? "")
            headers.set('RefreshToken', refreshToken ?? "")
            headers.set('Login', login ?? "")

            if (allotmentPerepectiveEnabled) {
                headers.set('AllotmentPerepectiveEnabled', allotmentPerepectiveEnabled)
            } else {
                headers.set('AllotmentPerepectiveEnabled', "false");
            }
        } catch (e) {
            console.log(e)
            logApiError('PREPARE_HEADERS_CRASH', 'N/A', {
                errorMessage: (e as any)?.message ?? String(e),
            })
        }

        const { siteId } = (getState() as any).commonReducer
        if (siteId) {
            let headerSiteId = String(siteId)
            if (isAllotmentPerspective) {
                if (!/_S$/i.test(headerSiteId)) {
                    headerSiteId = `${headerSiteId}_S`
                }
            } else {
                if (/_S$/i.test(headerSiteId)) {
                    headerSiteId = headerSiteId.replace(/_S$/i, '')
                }
            }
            headers.set('SiteID', headerSiteId)
        }

        // B2B SSO on /login: backend skips GetReauth when Referer is missing/stripped (site-config, etc.).
        // Must match Login.js session key ua_sso_extauth_params after LoginSessionID URL cleanup.
        if (typeof window !== 'undefined' && window.location?.pathname?.toLowerCase().includes('/login')) {
            try {
                const qs = new URLSearchParams(window.location.search)
                let hasSp = !!qs.get('SP')
                if (!hasSp) {
                    const raw = sessionStorage.getItem('ua_sso_extauth_params')
                    if (raw) {
                        const o = JSON.parse(raw) as { SP?: string }
                        hasSp = !!(o && o.SP)
                    }
                }
                if (hasSp) {
                    headers.set('X-B2B-SSO-Login-Page', '1')
                }
            } catch {
                /* ignore */
            }
        }

        headers.set('Content-Type', 'application/json');
        headers.set('Accept', 'application/json');
    },
    validateStatus: (response: any, body: any) => {
        return body.statusCode === 200 && response.status === 200;
    },
})

const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
> = retry(
    async (args, api, extraOptions) => {
        await mutex.waitForUnlock()
        
        // Check if site is in maintenance mode and abort API calls
        const { storefrontStatus } = (api.getState() as any).commonReducer
        if (storefrontStatus === 'maintenance') {
            const reqUrl = typeof args === 'string' ? args : (args as any)?.url
            logApiError('MAINTENANCE_MODE_BLOCK', reqUrl, {
                storefrontStatus,
            })
            return {
                error: {
                    status: 'MAINTENANCE_MODE',
                    data: {
                        message: 'Site is in maintenance mode'
                    }
                }
            }
        }

        const apiBase = (Config.ENV_ADMIN_BASE_URL ?? Config.ADMIN_BASE_URL) + Config.DYNAMIC_METHOD_SUB_URL
        if (args && (
            args.url == Config.END_POINT_NAMES.ADD_TO_CART ||
            args.url == Config.END_POINT_NAMES.CALCULATE_SHIPPING ||
            args.url == Config.END_POINT_NAMES.CHANGE_LINE_QUANTITY ||
            args.url == Config.END_POINT_NAMES.CONFIRM_ORDER ||
            args.url == Config.END_POINT_NAMES.EDIT_LINE_ITEM_PRODUCT ||
            args.url == Config.END_POINT_NAMES.GET_CART ||
            args.url == Config.END_POINT_NAMES.GET_CART_DETAIL ||
            args.url == Config.END_POINT_NAMES.GET_SHIPPING_METHODS ||
            args.url == Config.END_POINT_NAMES.PLACE_ORDER ||
            args.url == Config.END_POINT_NAMES.REMOVE_FROM_CART)) {
            const { CartID: cartId } = (api.getState() as any).userReducer.user
            if (!cartId) {
                const reqUrl = typeof args === 'string' ? args : (args as any)?.url
                logApiError('CART_OP_NO_CART_ID', reqUrl, {
                    action: 'force_logout',
                })
                api.dispatch(userActions.logOutUser({}))
                return {
                    error: {
                        status: '',
                        data: {
                            message: ''
                        }
                    }
                }
            }
        }

        let result = await baseQuery(apiBase)(args, api, extraOptions) as any
        const requestUrl = typeof args === 'string' ? args : args?.url
        const isSiteConfigRequest = requestUrl?.includes(Config.END_POINT_NAMES.SITE_CONFIG)
        const hasRetriedSiteConfig = (extraOptions as SiteConfigRetryExtraOptions | undefined)?.__siteConfigRetried === true

        // Guard: if site-config returned a 200 HTTP but the body carries an error statusCode,
        // treat it as a retryable error for one explicit site-config retry.
        if (!result.error && result.data?.statusCode && result.data.statusCode !== 200 && isSiteConfigRequest) {
            console.warn('site-config responded with non-200 body statusCode:', result.data.statusCode)
            logApiError('SITE_CONFIG_BODY_ERROR', requestUrl, {
                bodyStatusCode: result.data.statusCode,
                errorMessage: result.data.errorMessage ?? result.data.statusMessage ?? '',
                hasRetriedSiteConfig,
            })
            result = {
                error: {
                    status: result.data.statusCode,
                    data: result.data,
                }
            }
        }

        if (
            isSiteConfigRequest &&
            !hasRetriedSiteConfig &&
            (result?.error?.status === 'FETCH_ERROR' || Number(result?.error?.status) === 501)
        ) {
            logApiError('SITE_CONFIG_RETRY', requestUrl, {
                firstAttemptErrorStatus: result?.error?.status,
                firstAttemptErrorMessage: result?.error?.data?.errorMessage ?? result?.error?.error ?? '',
            })
            result = await baseQuery(apiBase)(args, api, {
                ...(extraOptions as object),
                __siteConfigRetried: true,
            } as SiteConfigRetryExtraOptions)
            if (result.error) {
                logApiError('SITE_CONFIG_RETRY_ALSO_FAILED', requestUrl, {
                    retryErrorStatus: result.error?.status,
                    retryErrorMessage: result.error?.data?.errorMessage ?? result.error?.error ?? '',
                    retryBodyStatusCode: result.error?.data?.statusCode,
                })
            }
        }

        if (result.error && result.error.data?.statusCode === 501 && requestUrl?.includes('slas/extauth')) {
            logApiError('SSO_EXTAUTH_501', requestUrl, {
                errorMessage: result.error?.data?.errorMessage,
                statusMessage: result.error?.data?.statusMessage,
            })
            return {
                error: {
                    status: result.error?.data.statusCode,
                    data: {
                        message: result.error?.data.errorMessage,
                        statusMessage: result.error?.data.statusMessage,
                    }
                }
            }
        }
        if (result.error && result.error.data?.statusCode === 501) {
            let regex = Config.ERROR_MESSAGES.HTTP501_NO_BASKET_REGEX
            if (!mutex.isLocked()) {
                const release = await mutex.acquire()
                try {
                    if (regex.test(result.error.data?.errorMessage)) {
                        const newCart = await baseQuery(apiBase)(typeof args === 'string' ? Config.END_POINT_NAMES.CREATE_BASKET : {
                            ...args,
                            url: Config.END_POINT_NAMES.CREATE_BASKET
                        }, api, extraOptions) as any

                        if (newCart && newCart.data && newCart.data.data) {
                            api.dispatch(cartActions.setCart(newCart.data.data))
                            api.dispatch(userActions.rotateCart(newCart.data.data.id))

                            // retry the initial query
                            result = await baseQuery(apiBase)(args, api, extraOptions)
                        }
                    } else if (Config.ERROR_MESSAGES.HTTP501_CARTID_MISSING === result.error.data?.errorMessage ||
                        Config.ERROR_MESSAGES.HTTP501_UNKNOWN_BASKET_ERROR === result.error.data?.errorMessage) {
                        logApiError('HTTP501_BASKET_FATAL', requestUrl, {
                            errorMessage: result.error.data?.errorMessage,
                            action: 'force_logout',
                        })
                        api.dispatch(userActions.logOutUser({ flashMessage: Config.ERROR_MESSAGES.HTTP501_UNKNOWN_BASKET_ERROR_MSG }))
                        retry.fail(result.error)
                    } else if (result.error.data?.errorMessage === 'invalid refresh_token') {
                        // Stale SLAS refresh token — the user's SSO session has expired or been
                        // invalidated. Force logout so subsequent calls do not keep retrying reauth
                        // with a dead token. The user will need to re-authenticate via SSO.
                        logApiError('HTTP501_INVALID_REFRESH_TOKEN', requestUrl, {
                            errorMessage: result.error?.data?.errorMessage,
                            action: 'force_logout',
                        })
                        api.dispatch(userActions.logOutUser({}))
                        retry.fail(result.error)
                    } else {
                        logApiError('HTTP501_UNHANDLED', requestUrl, {
                            errorMessage: result.error?.data?.errorMessage,
                        })
                        return {
                            error: {
                                status: result.error?.data.statusCode,
                                data: {
                                    message: result.error?.data.errorMessage,
                                }
                            }
                        }
                    }
                } finally {
                    // release must be called once the mutex should be released again.
                    release()
                }
            }
        } else if (result.error && result.error.data?.statusCode === 401) {
            logApiError('HTTP401_UNAUTHORIZED', requestUrl, {
                errorMessage: result.error.data?.errorMessage,
                action: 'force_logout',
            })
            if (!mutex.isLocked()) {
                const release = await mutex.acquire()
                try {
                    // const refreshResult = await baseQuery(apiBase)(typeof args === 'string' ? Config.END_POINT_NAMES.REFRESH_TOKEN : {
                    //     ...args,
                    //     url: Config.END_POINT_NAMES.REFRESH_TOKEN
                    // }, api, extraOptions) as any
                    // const token = refreshResult.data.data?.[0].JWTToken
                    // if (token) {
                    //     api.dispatch(userActions.setNewToken(token))
                    //     // retry the initial query
                    //     result = await baseQuery(apiBase)(args, api, extraOptions)
                    // } else {
                    api.dispatch(userActions.logOutUser({}))
                    retry.fail(result.error)
                    // }
                } finally {
                    // release must be called once the mutex should be released again.
                    release()
                }
            } else {
                // wait until the mutex is available without locking it
                await mutex.waitForUnlock()
                result = await baseQuery(apiBase)(args, api, extraOptions)
            }
        } else if (result.error && result.error.status === 'FETCH_ERROR') {
            logFetchError(requestUrl, result.error)
            if (!isSiteConfigRequest) {
                console.log('Backend offline...')
                retry.fail(result.error)
            }
        } else if (result.error && result.error.data?.statusCode !== 200) {
            logApiError('HTTP_NON200', requestUrl, {
                statusCode: result.error?.data?.statusCode,
                errorMessage: result.error?.data?.errorMessage,
            })
            return {
                error: {
                    status: result.error?.data.statusCode,
                    data: {
                        message: result.error?.data.errorMessage,
                    }
                }
            }
        }
        return result
    },
    { maxRetries: 0 }
)

/**
 * Create a base API to inject endpoints into elsewhere.
 * Components using this API should import from the injected site,
 * in order to get the appropriate types,
 * and to ensure that the file injecting the endpoints is loaded 
 */
export const api = createApi({
    /**
     * `reducerPath` is optional and will not be required by most users.
     * This is useful if you have multiple API definitions,
     * e.g. where each has a different domain, with no interaction between endpoints.
     * Otherwise, a single API definition should be used in order to support tag invalidation,
     * among other features
     */
    reducerPath: 'adminApi',
    /**
     * A bare bones base query would just be `baseQuery: fetchBaseQuery({ baseUrl: '/' })`
     */
    baseQuery: baseQueryWithReauth,
    /**
     * Tag types must be defined in the original API definition
     * for any tags that would be provided by injected endpoints
     */
    tagTypes: ['Product', 'Products', 'Cart', 'ProductHowToMeasure', 'SiteInfo', 'HomeBanner', 'ShippingMethods', 'PaymentMethods', 'Locales', 'Categories', 'SmartySuggestions', 'CustomerAddresses', 'User', 'ProductAttributes', 'LoginExt'],
    /**
     * This api has endpoints injected in adjacent files,
     * which is why no endpoints are shown below.
     * If you want all endpoints defined in the same file, they could be included here instead
     */
    endpoints: () => ({})
})

export const enhancedApi = api.enhanceEndpoints({
    endpoints: () => ({
        getProduct: () => 'test',
    }),
})
